import { useFormDataQuery } from '@/api/getters';
import { FormControl, FormField, FormItem } from '@/components/ui/Form';
import { Input } from '@/components/ui/Input';
import { OWNER_ACCOUNT_IS_OWNER_NAME } from '@/features/v2/summaryForms/components/CheckboxAccountIsOwner';
import { OWNER_EMAIL_NAME } from '@/features/v2/summaryForms/components/InputEmail';
import { validatePhone } from '@/features/v2/summaryForms/Validate';

import { useLingui } from '@lingui/react';
import { useFormContext } from 'react-hook-form';

export const OWNER_PHONE_NAME = 'owner.phone';

interface ErrorOwnerFields {
  phone?: string;
  email?: string;
}

const InputPhone = ({
  name = OWNER_PHONE_NAME,
  focused,
  index,
  setFocused,
  autoCompleteOff = false,
  disabled = false,
}: {
  name: string;
  focused: string;
  index?: number;
  setFocused: (value: string) => void;
  autoCompleteOff?: boolean;
  disabled?: boolean;
}) => {
  const { i18n } = useLingui();
  const { trigger } = useFormContext();
  const { watch, formState } = useFormContext();
  const { data } = useFormDataQuery();

  const emailFieldName =
    name === OWNER_PHONE_NAME ? OWNER_EMAIL_NAME : `coowners.${index}.email`;

  const otherPhones =
    name === OWNER_PHONE_NAME
      ? []
      : [watch(`coowners.${(index ?? 0) - 1}.phone`), watch(OWNER_PHONE_NAME)];

  const isAccountOwner =
    !!watch(OWNER_ACCOUNT_IS_OWNER_NAME) && name === OWNER_PHONE_NAME;

  const isEmailAdded = watch(emailFieldName);

  const emailFieldError =
    name === OWNER_PHONE_NAME && index
      ? (formState.errors.owner as ErrorOwnerFields)?.email
      : (formState.errors.coowners as unknown as ErrorOwnerFields[])?.[
          index ?? 0
        ]?.email;

  return (
    <FormField
      name={name}
      rules={{
        validate: (value: string) =>
          validatePhone({
            value,
            otherPhones,
            accountPhone: isAccountOwner ? null : data?.user?.phone,
            isOptional: isEmailAdded,
          }),
      }}
      defaultValue={isAccountOwner ? data?.user?.phone : ''}
      render={({ field, fieldState }) => {
        const { value, onChange, onBlur, ...restField } = field;
        return (
          <FormItem className="col-span-4 md:col-span-2">
            <FormControl>
              <Input
                autoComplete={autoCompleteOff ? 'none' : 'tel'}
                value={value}
                label={i18n.t('Nr telefonu')}
                error={fieldState.error?.message}
                disabled={disabled}
                success={
                  focused !== field.name && !fieldState.error && field.value
                }
                onChange={(e) => {
                  if (!/^[0-9+ -]*$/.test(e.target.value)) return;
                  onChange(e);
                  if (fieldState.error) {
                    trigger(field.name);
                  }
                }}
                onBlur={() => {
                  setFocused('');
                  emailFieldError && trigger(emailFieldName);
                  onBlur();
                }}
                onFocus={() => setFocused(field.name)}
                {...restField}
              />
            </FormControl>
          </FormItem>
        );
      }}
    />
  );
};

export default InputPhone;
